<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import QuantitySelector from '@/components/cart/QuantitySelector.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import money from '@/filters/money';
import { BYOB_PRODUCT_KEY, BYOB_PRODUCT_SKU } from '@/lib/customizers/boxes';
import { SkuSelection } from '@/lib/customizers/common';
import { CustomProductType, DigitalGifting } from '@/lib/customizers/digitalGifting';
import { NutsAddToCartItem, sendProductAddedEvent } from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import { Money } from '@/utils/money';

const props = defineProps<{
  boxContent: (SkuSelection | undefined)[];
  digitalGiftingProduct?: DigitalGifting;
  priceBeforeDiscount: Money;
  totalPrice: Money;
}>();

const emit = defineEmits(['remove', 'open-modal']);

const router = useRouter();
const selectedQuantity = defineModel<number>('quantity', { required: true });

const dynamicBundleSkus = computed(() => props.boxContent.filter((c) => !!c).map((c) => c.sku));

const store = useStore();
const { addToCart, cart } = useCart(store);

async function addItemToCart() {
  await addToCart(
    {
      cart_sku: {
        dynamic_bundle_skus: dynamicBundleSkus.value,
        quantity: selectedQuantity.value,
        sku_external_id: BYOB_PRODUCT_SKU,
      },
    },
    { router, skipCartAdded: true },
  );
}

function buildProductAddedPayload(): NutsAddToCartItem {
  return {
    cartId: getCartId(cart.value),
    markedAsGift: false,
    name: 'Build Your Own Box',
    pageSection: 'Buy Block',
    price: props.totalPrice,
    priceBeforeDiscount: props.priceBeforeDiscount,
    productId: BYOB_PRODUCT_KEY,
    quantity: selectedQuantity.value,
    reportingCategory: 'Gifts > Custom Box',
    sku: BYOB_PRODUCT_SKU,
    weight: 2,
  };
}

const handleAddToCart = useCallback(async () => {
  const payload = buildProductAddedPayload();
  sendProductAddedEvent(payload);

  await addItemToCart();
});

const filledIn = (index: number) => !!props.boxContent?.[index];

const getBorderClass = (index: number) => {
  let border = 'border';

  switch (index) {
    case 0:
      border += ' rounded-tl-lg';
      break;
    case 2:
      border += ' rounded-tr-lg';
      break;
    case 3:
      border += ' rounded-bl-lg';
      break;
    case 5:
      border += ' rounded-br-lg';
      break;
    default:
      break;
  }

  return border;
};

const handleRemove = (slot: number) => {
  emit('remove', props.boxContent[slot]?.productKey);
};

const handleOpenModal = () => {
  emit('open-modal');
};

const canAddToCart = computed(() => !props.boxContent.some((item) => !item));

const isDigitalGifting = computed(() => !!props.digitalGiftingProduct?.giftCertificateCode);

const handleGoToShippingDigitalGift = () => {
  if (props.digitalGiftingProduct) {
    const digitalGiftedProduct = {
      ...props.digitalGiftingProduct,
      giftedSku: props.digitalGiftingProduct.digitalGiftSku,
      ingredients: props.boxContent,
      size: props.digitalGiftingProduct.giftedProduct.sku,
      type: CustomProductType.Box,
    };

    sessionStorage.setItem('digitalGiftedProduct', JSON.stringify(digitalGiftedProduct));
    router.push('/gift-drop/redeem/address');
  }
};

const {
  'static_img/custom-builder/byob_box_image.png': box,
  'img/close.svg': closeIcon,
  'static_img/custom-builder/Icon-1.svg': icon0,
  'static_img/custom-builder/Icon-2.svg': icon1,
  'static_img/custom-builder/Icon-3.svg': icon2,
  'static_img/custom-builder/Icon-4.svg': icon3,
  'static_img/custom-builder/Icon-5.svg': icon4,
  'static_img/custom-builder/Icon-6.svg': icon5,
  'img/info-icon-filled.svg': infoIconFilled,
} = nutshell;

const tooltipTriggerUtilities = [
  'bg-black',
  'cursor-pointer',
  'h-3.5',
  'lg:h-4',
  'rounded-full',
  'text-tiny',
  'text-white',
  'w-3.5',
  'lg:w-4',
];
</script>

<template>
  <div class="flex flex-col items-center px-6 pt-4 pb-40 lg:pb-0 lg:sticky lg:top-2">
    <div class="relative flex justify-center" data-test="buyblock-builder-image">
      <img :src="box" class="w-4/5 mb-4" alt="BYOB" />
      <div class="tiny-note popover-container">
        <PopperWrapper
          arrow
          class="absolute top-1 right-20 legacy-max-width"
          offsetDistance="10"
          placement="bottom"
        >
          <template v-slot:content>
            <div class="p-3 text-xs w-36 text-neutral-500" data-test="byob-popper">
              Image is for reference only. Color of internal packaging may vary.
            </div>
          </template>
          <img
            :class="tooltipTriggerUtilities"
            :src="infoIconFilled"
            alt="BYOB info"
            aria-describedby="byob-popper-locator"
            class="ml-2 transform -translate-y-0.5"
            data-test="byob-popover"
            role="button"
          />
        </PopperWrapper>
        <span id="cvv-popper-locator" class="sr-only"> Box info is shown in a tooltip </span>
      </div>
    </div>
    <div class="hidden mb-5 text-base text-center 2xl:inline-block text-neutral-500 w-72">
      Select ingredients on the left to fill the box with something yummy!
    </div>
    <button
      class="flex items-center p-0 mb-6 text-base text-black bg-transparent border-none cursor-pointer font-proxima-nova lg:hidden"
      @click.prevent="handleOpenModal"
    >
      <span
        class="flex items-center justify-center w-5 h-5 mr-2 text-lg font-semibold text-white bg-black rounded-full"
      >
        +
      </span>
      Select Items
    </button>
    <div class="grid content-grid" data-test="box-content-grid">
      <div
        class="relative flex justify-center p-2 -mb-px -mr-px border border-neutral-300 align-items"
        :class="[getBorderClass(0), filledIn(0) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[0]?.imageUrl ?? icon0"
          :class="filledIn(0) ? 'object-contain' : 'p-2'"
          alt="box-item"
          data-test="box-item"
        />
        <img
          v-if="filledIn(0)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(0)"
          @keydown.enter="handleRemove(0)"
          tabindex="0"
          alt="remove"
          data-test="remove-item"
        />
      </div>
      <div
        class="relative flex justify-center p-2 -mb-px -mr-px border border-neutral-300 align-items"
        :class="[getBorderClass(1), filledIn(1) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[1]?.imageUrl ?? icon1"
          :class="filledIn(1) ? 'object-contain' : 'p-2'"
          alt="box-item"
        />
        <img
          v-if="filledIn(1)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(1)"
          @keydown.enter="handleRemove(1)"
          tabindex="0"
          alt="remove"
        />
      </div>
      <div
        class="relative flex justify-center p-2 -mb-px border border-neutral-300 align-items"
        :class="[getBorderClass(2), filledIn(2) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[2]?.imageUrl ?? icon2"
          :class="filledIn(2) ? 'object-contain' : 'p-2'"
          alt="box-item"
        />
        <img
          v-if="filledIn(2)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(2)"
          @keydown.enter="handleRemove(2)"
          tabindex="0"
          alt="remove"
        />
      </div>
      <div
        class="relative flex justify-center p-2 -mr-px border border-neutral-300 align-items"
        :class="[getBorderClass(3), filledIn(3) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[3]?.imageUrl ?? icon3"
          :class="filledIn(3) ? 'object-contain' : 'p-2'"
          alt="box-item"
        />
        <img
          v-if="filledIn(3)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(3)"
          @keydown.enter="handleRemove(3)"
          tabindex="0"
          alt="remove"
        />
      </div>
      <div
        class="relative flex justify-center p-2 -mr-px border border-neutral-300 align-items"
        :class="[getBorderClass(4), filledIn(4) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[4]?.imageUrl ?? icon4"
          :class="filledIn(4) ? 'object-contain' : 'p-2'"
          alt="box-item"
        />
        <img
          v-if="filledIn(4)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(4)"
          @keydown.enter="handleRemove(4)"
          tabindex="0"
          alt="remove"
        />
      </div>
      <div
        class="relative flex justify-center p-2 border border-neutral-300 align-items"
        :class="[getBorderClass(5), filledIn(5) ? 'border-solid' : 'border-dashed']"
      >
        <img
          :src="boxContent[5]?.imageUrl ?? icon5"
          :class="filledIn(5) ? 'object-contain' : 'p-2'"
          alt="box-item"
        />
        <img
          v-if="filledIn(5)"
          :src="closeIcon"
          class="shadow-lg bg-white rounded-full w-6 p-1.5 absolute top-1 right-1 cursor-pointer"
          @click="handleRemove(5)"
          @keydown.enter="handleRemove(5)"
          tabindex="0"
          alt="remove"
        />
      </div>
    </div>

    <div v-if="isDigitalGifting" class="w-full mt-6 lg:mt-10 lg:w-72">
      <ThemedButton
        theme="gray"
        :disabled="!canAddToCart"
        fullWidth
        @click="handleGoToShippingDigitalGift"
      >
        Continue to Shipping Address
      </ThemedButton>
    </div>

    <template v-else>
      <div class="flex items-end justify-between w-full mt-6 lg:mt-10 lg:w-72">
        <div class="w-full lg:w-1/2">
          <span class="text-xs font-semibold text-neutral-600">Quantity</span>
          <QuantitySelector v-model="selectedQuantity" data-test="custom-box-quantity" />
        </div>
        <div
          class="hidden mb-3 text-base font-semibold text-right lg:inline-block"
          data-test="custom-box-total-amount"
        >
          Total: {{ money(totalPrice) }}
        </div>
      </div>
      <div
        class="fixed bottom-0 right-0 z-40 flex items-center justify-center w-full p-2 bg-white shadow-b lg:relative"
      >
        <div class="mr-4 lg:hidden">
          <div class="text-sm font-semibold" data-test="custom-box-total-amount-mobile">
            {{ money(totalPrice) }}
          </div>
          <div class="text-xs text-gray-400">Qty: {{ selectedQuantity }}</div>
        </div>
        <ThemedButton
          class="mt-6 mb-6 w-72 lg:mt-4 lg:mb-0"
          data-test="add-to-cart-button"
          :disabled="!canAddToCart"
          :isLoading="handleAddToCart.isPending"
          theme="gray"
          @click="handleAddToCart.execute"
        >
          <SmallBodyText class="whitespace-nowrap"> Add to Cart </SmallBodyText>
        </ThemedButton>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.hidden {
  visibility: visible;
}
.popover-container {
  :deep(.popper__arrow) {
    margin-left: 0;
  }
}

.content-grid {
  grid-template-columns: repeat(3, minmax(0, 8rem));
  grid-template-rows: repeat(2, minmax(0, 7rem));

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 6rem));
    grid-template-rows: repeat(2, minmax(0, 6rem));
  }
}

@media screen and (max-width: 768px) {
  .shadow-b {
    box-shadow: 0px 0px 19px 3px rgba(161, 161, 161, 0.5);
  }
}
</style>
